import {
  Alert,
  Link,
  Loader,
  PageHeader,
  Stack,
  Text,
  Checkbox,
  Button,
  Divider,
  useNotify,
  Field,
} from '@hotjar/hotsauce';
import { Trans, useHelpHotjarUrl, useTranslation } from '@hotjar/translations';
import { useWindowTitle } from '@hotjar/webapp/hooks/useWindowTitle';
import { useCurrentUser } from '@hotjar/user';
import type { Dispatch, FormEvent, SetStateAction } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import * as Sentry from '@sentry/browser';
import { mutateProfileNotificationSettings } from './query';
import { AlertWrapper, Page, Wrapper } from './styles';
import { JSError } from './utils';

const SUPPORT_LINK = '/requests/new';

const toggleCheckbox = (setState: Dispatch<SetStateAction<boolean | undefined>>) => {
  setState((val) => !(val ?? false));
};

export function ProfileNotifications(): JSX.Element | null {
  const { t } = useTranslation('profile', { keyPrefix: 'notifications' });

  useWindowTitle(t('page-title'));

  const helpHotjarUrl = useHelpHotjarUrl();

  const { showToast } = useNotify();

  const { isLoading: isLoadingCurrentUser, data: currentUserData, refetch } = useCurrentUser();

  const [receiveAnalysisEmails, setReceiveAnalysisEmails] = useState(
    currentUserData?.receiveAnalysisEmails,
  );
  const [receiveFeedbackEmails, setReceiveFeedbackEmails] = useState(
    currentUserData?.receiveFeedbackEmails,
  );

  useEffect(() => {
    if (
      currentUserData?.receiveAnalysisEmails !== undefined &&
      receiveAnalysisEmails === undefined
    ) {
      setReceiveAnalysisEmails(currentUserData.receiveAnalysisEmails);
    }
  }, [currentUserData?.receiveAnalysisEmails, receiveAnalysisEmails]);

  useEffect(() => {
    if (
      currentUserData?.receiveFeedbackEmails !== undefined &&
      receiveFeedbackEmails === undefined
    ) {
      setReceiveFeedbackEmails(currentUserData.receiveFeedbackEmails);
    }
  }, [currentUserData?.receiveFeedbackEmails, receiveFeedbackEmails]);

  const shouldShowEmailBlockedAlert = currentUserData?.emailBlocked ?? false;

  const handleJSError = useCallback(
    (error: unknown) => {
      if (error instanceof JSError) {
        showToast({
          kind: 'danger',
          text: t('generic-error'),
        });
        Sentry.captureException(error, {
          tags: {
            squad: 'security-and-auth',
            criticality: 'minor',
            featureName: 'profile.notifications',
          },
        });
      } else {
        showToast({
          kind: 'danger',
          text: t('generic-error'),
        });
      }
    },
    [t, showToast],
  );

  const { mutate, isLoading: isLoadingMutation } = useMutation(mutateProfileNotificationSettings, {
    onSuccess() {
      refetch();
    },
    onError: handleJSError,
  });

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (
        currentUserData?.id === undefined ||
        receiveAnalysisEmails === undefined ||
        receiveFeedbackEmails === undefined
      ) {
        handleJSError(
          new JSError(
            'Error updating profile notification settings! Api not called due to missing data.',
          ),
        );
        return;
      }
      mutate({
        userId: currentUserData?.id,
        receiveAnalysisEmails,
        receiveFeedbackEmails,
      });
    },
    [receiveAnalysisEmails, receiveFeedbackEmails, currentUserData?.id, mutate, handleJSError],
  );

  const createNewTicket = useCallback(() => {
    window.open(`${helpHotjarUrl}${SUPPORT_LINK}`, '_blank', 'noopener noreferrer');
  }, [helpHotjarUrl]);

  if (isLoadingCurrentUser) {
    return <Loader size="xl" />;
  }

  return (
    <Page>
      <PageHeader title={t('page-title')} />
      {shouldShowEmailBlockedAlert && typeof currentUserData?.email === 'string' && (
        <AlertWrapper>
          <Alert kind="warning">
            <Text textType="body1">
              <Trans
                t={t}
                i18nKey="blocked-email-message"
                values={{ email: currentUserData.email }}
                components={[
                  <br />,
                  <Link kind="inline" as="button" onClick={createNewTicket}>
                    {/* This is replaced by Trans, it's required as Link expects children */}
                    ...
                  </Link>,
                ]}
              />
            </Text>
          </Alert>
        </AlertWrapper>
      )}
      <Wrapper>
        <form onSubmit={handleSubmit}>
          <Stack preset="form">
            <Field
              size="full"
              titleAs="label"
              helperText={t(`analysis-emails-help-text`)}
              inputId="receiveAnalysisEmails"
              labelText={t('analysis-emails-label')}
            >
              <Checkbox
                id="receiveAnalysisEmails"
                labelContent={t('analysis-emails-input-label')}
                onChange={() => toggleCheckbox(setReceiveAnalysisEmails)}
                isChecked={receiveAnalysisEmails}
              />
            </Field>
            <Field
              size="full"
              titleAs="label"
              helperText={t(`feedback-emails-help-text`)}
              inputId="receiveFeedbackEmails"
              labelText={t('feedback-emails-label')}
            >
              <Checkbox
                id="receiveFeedbackEmails"
                labelContent={t('feedback-emails-input-label')}
                onChange={() => toggleCheckbox(setReceiveFeedbackEmails)}
                isChecked={receiveFeedbackEmails}
              />
            </Field>
          </Stack>
          <Divider spacing="m" />
          <Button
            kind="primary"
            size="m"
            isLoading={isLoadingMutation}
            isDisabled={isLoadingMutation}
            type="submit"
          >
            {t('form-submit')}
          </Button>
        </form>
      </Wrapper>
    </Page>
  );
}
